import React, { useState, useEffect } from 'react';
import { Table, Form, Input, Button, Space, Select, Tag, message } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { getImportRecords } from '../../api/serial';
import { useVisibleFields, hasPerm } from '../../hooks/usePermission';

const PLATFORM_OPTIONS = ['电信', '移动', '联通', '涂鸦'].map((v) => ({ value: v, label: v }));

const ImportRecords = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

    const fetchList = async (params = {}) => {
        setLoading(true);
        try {
            const res = await getImportRecords({ page: params.page || 1, page_size: params.page_size || 10, ...params });
            if (res.code === 0) {
                setDataSource(res.data.list);
                setPagination((prev) => ({ ...prev, total: res.data.total }));
            } else {
                message.error(res.message || '获取列表失败');
            }
        } catch (error) {
            console.error('Fetch import records failed:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
    }, []);

    const handleSearch = () => {
        fetchList({ ...form.getFieldsValue(), page: 1 });
    };

    const handleReset = () => {
        form.resetFields();
        fetchList({ page: 1 });
    };

    const downloadImportDetail = (record) => {
        const summary = [
            ['文件名', record.file_name],
            ['平台', record.platform],
            ['操作人', record.operator],
            ['导入时间', record.import_time],
            ['导入数量', record.total],
            ['成功条数', record.success],
            ['失败条数', record.fail],
        ];

        const successHeader = ['SN', 'MAC', 'C*EI', 'UID', 'KEY'];
        const successRows = Array.from({ length: record.success }).map((_, i) => [
            `SN${String(100000 + i + 1)}`,
            `E0DCA0${String(i + 1).padStart(6, '0').toUpperCase()}`,
            `${String(860000000000001 + i)}`,
            `UID${String(800000 + i)}`,
            `KEY${String(1000 + i)}`,
        ]);

        const failHeader = ['SN', 'MAC', 'C*EI', '失败原因'];
        const failRows = Array.from({ length: record.fail }).map((_, i) => [
            `SN${String(200000 + i + 1)}`,
            '',
            '',
            record.void_type,
        ]);

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), '导入汇总');
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([successHeader, ...successRows]), '成功导入');
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([failHeader, ...failRows]), '导入失败');
        XLSX.writeFile(wb, `${record.file_name.replace(/\.xlsx?$/i, '')}_导入详情.xlsx`);
    };

    const visibleFields = useVisibleFields('serial:import-records');
    const show = (code) => visibleFields.has(code);

    const columns = [
        show('file_name') && { title: '文件名', dataIndex: 'file_name', key: 'file_name' },
        show('platform') && { title: '平台', dataIndex: 'platform', key: 'platform', width: 90 },
        show('operator') && { title: '操作人', dataIndex: 'operator', key: 'operator', width: 120 },
        show('import_time') && { title: '导入时间', dataIndex: 'import_time', key: 'import_time', width: 180 },
        show('total') && { title: '导入数量', dataIndex: 'total', key: 'total', width: 100 },
        show('success') && { title: '成功', dataIndex: 'success', key: 'success', width: 80, render: (v) => <Tag color="success">{v}</Tag> },
        show('fail') && { title: '失败', dataIndex: 'fail', key: 'fail', width: 80, render: (v) => <Tag color={v ? 'error' : 'default'}>{v}</Tag> },
        {
            title: '操作', key: 'action', width: 160,
            onHeaderCell: () => ({ style: { textAlign: 'center' } }),
            render: (_, record) => (
                <Space>
                    {hasPerm('serial:import:download') && (
                        <Button type="link" onClick={() => downloadImportDetail(record)}>下载</Button>
                    )}
                    {hasPerm('serial:import:fail-detail') && (
                        <Button
                            type="link"
                            onClick={() => navigate(`/serial/void-records?void_type=${encodeURIComponent(record.void_type)}&void_time=${encodeURIComponent(record.import_time)}`)}
                        >
                            失败详情
                        </Button>
                    )}
                </Space>
            ),
        },
    ].filter(Boolean);

    return (
        <div style={{ padding: '16px' }}>
            <Form form={form} layout="inline" style={{ marginBottom: 16, rowGap: 16 }}>
                <Form.Item name="file_name">
                    <Input placeholder="文件名" allowClear style={{ width: 200 }} />
                </Form.Item>
                <Form.Item name="platform">
                    <Select placeholder="平台" allowClear options={PLATFORM_OPTIONS} style={{ width: 120 }} />
                </Form.Item>
                <Form.Item>
                    <Space>
                        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
                        <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
                    </Space>
                </Form.Item>
            </Form>

            <Table
                rowKey="id"
                columns={columns}
                dataSource={dataSource}
                loading={loading}
                pagination={pagination}
            />
        </div>
    );
};

export default ImportRecords;
